import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './student-dashboard-responsive.css';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const PUBLIC_TEST_INSTRUCTIONS_API =
    process.env.REACT_APP_PUBLIC_TEST_INSTRUCTIONS_API || `${API_BASE_URL}/api/admin-app/public-test-instructions/`;
const SECURITY_SETTINGS_API =
    process.env.REACT_APP_SECURITY_SETTINGS_API || `${API_BASE_URL}/api/admin-app/security-settings/`;

const StudentTestInstructionsMain = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const quizId = (params.get('quiz_id') || '').trim();
    const [instructions, setInstructions] = useState([]);
    const [isLoadingInstructions, setIsLoadingInstructions] = useState(false);
    const [instructionTimerEnabled, setInstructionTimerEnabled] = useState(true);
    const [mediaStatus, setMediaStatus] = useState('idle');
    const [mediaMessage, setMediaMessage] = useState('');
    const [timeRemaining, setTimeRemaining] = useState(180);
    const instructionCount = instructions.length;

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    useEffect(() => {
        let isMounted = true;

        const requestMedia = async () => {
            try {
                setMediaStatus('requesting');
                setMediaMessage('');
                const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
                stream.getTracks().forEach((track) => track.stop());
                if (!isMounted) return;
                sessionStorage.setItem('exam_media_ready', 'true');
                setMediaStatus('granted');
                setMediaMessage('Camera and microphone access granted.');
            } catch (error) {
                if (!isMounted) return;
                sessionStorage.removeItem('exam_media_ready');
                setMediaStatus('denied');
                setMediaMessage('Camera and microphone access are required to start the exam.');
            }
        };

        requestMedia();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!instructionTimerEnabled) {
            setTimeRemaining(0);
            return undefined;
        }

        const timer = setInterval(() => {
            setTimeRemaining((prev) => Math.max(prev - 1, 0));
        }, 1000);
        return () => clearInterval(timer);
    }, [instructionTimerEnabled]);

    useEffect(() => {
        let isMounted = true;

        const fetchSecuritySettings = async () => {
            try {
                const response = await fetch(SECURITY_SETTINGS_API);
                const data = response.ok ? await response.json() : null;
                if (!isMounted || !data) return;
                setInstructionTimerEnabled(data.instruction_timer_enabled !== false);
            } catch (error) {
                if (!isMounted) return;
                setInstructionTimerEnabled(true);
            }
        };

        fetchSecuritySettings();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;

        const fetchInstructions = async () => {
            try {
                setIsLoadingInstructions(true);
                const response = await fetch(PUBLIC_TEST_INSTRUCTIONS_API);
                const data = response.ok ? await response.json() : [];
                if (!isMounted) return;
                setInstructions(Array.isArray(data) ? data : []);
            } catch (error) {
                if (!isMounted) return;
                setInstructions([]);
            } finally {
                if (isMounted) {
                    setIsLoadingInstructions(false);
                }
            }
        };

        fetchInstructions();

        return () => {
            isMounted = false;
        };
    }, []);

    const handleStartTest = () => {
        // open declaration modal instead of immediately starting
        if (!quizId) return;
        setShowDeclarationModal(true);
    };

    const [showDeclarationModal, setShowDeclarationModal] = useState(false);

    const proceedStartTest = () => {
        if (!quizId) return;
        sessionStorage.setItem(`exam_instructions_seen_${quizId}`, 'true');
        setShowDeclarationModal(false);
        navigate(`/dashboard/student/test-attempt?role=student&quiz_id=${encodeURIComponent(quizId)}`);
    };

    return (
        <div className="student-quiz-fullscreen">
            <div className="student-quiz-topbar">
                <div className="student-quiz-title-text">Test Instructions</div>
                <div className="student-quiz-topbar-right">
                    <Link to="/dashboard/student?role=student" className="student-quiz-btn outline student-quiz-topbar-back">
                        Back
                    </Link>
                </div>
            </div>

            <div className="student-quiz-sections">
                <div className="student-quiz-sections-label">Before you start</div>
                <div className="student-quiz-sections-list">
                    <div className="student-quiz-section-pill is-active">
                        <span>Instructions</span>
                        <span className="section-badge">{instructionCount}</span>
                    </div>
                    <div className="student-quiz-section-pill is-locked">
                        <span>Test</span>
                        <span className="section-badge">1</span>
                    </div>
                </div>
            </div>

            {!quizId ? (
                <div className="student-quiz-loading" role="alert">
                    Quiz ID not found. Go back to dashboard and start again.
                </div>
            ) : (
                <div className="student-quiz-body">
                    <main className="student-quiz-main student-quiz-main-instructions">
                        <div className="student-quiz-center student-quiz-center-instructions">
                            <div className="student-quiz-panel">
                                <div className="student-quiz-question-title">
                                    Instructions {isLoadingInstructions ? '' : `(${instructionCount} items)`}
                                </div>
                                <div className="student-instructions-shell">
                                    {isLoadingInstructions ? (
                                        <div className="student-quiz-loading student-instructions-loading">Loading instructions...</div>
                                    ) : instructions.length > 0 ? (
                                        <ul className="student-instruction-list student-instruction-list-large">
                                            {instructions.map((item, index) => (
                                                <li key={item.id || item.title} className="student-instruction-list-item">
                                                    <span className="student-instruction-number">{index + 1}</span>
                                                    <span className="student-instruction-text">{item.title}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className="student-quiz-warning student-instructions-empty">
                                            No active test instructions are available right now.
                                        </div>
                                    )}
                                </div>

                                <div className="student-instructions-actions">
                                    {mediaMessage && (
                                        <div
                                            className={`student-instructions-status ${
                                                mediaStatus === 'granted' ? 'is-good' : 'is-warn'
                                            }`}
                                            role="alert"
                                        >
                                            {mediaMessage}
                                        </div>
                                    )}
                                    <div className="student-quiz-footer-actions student-instructions-footer-actions">
                                        <button
                                            type="button"
                                            className="student-quiz-btn outline"
                                            onClick={() => {
                                                setMediaStatus('idle');
                                                setMediaMessage('');
                                                sessionStorage.removeItem('exam_media_ready');
                                                navigator.mediaDevices
                                                    .getUserMedia({ video: true, audio: true })
                                                    .then((stream) => {
                                                        stream.getTracks().forEach((track) => track.stop());
                                                        sessionStorage.setItem('exam_media_ready', 'true');
                                                        setMediaStatus('granted');
                                                        setMediaMessage('Camera and microphone access granted.');
                                                    })
                                                    .catch(() => {
                                                        sessionStorage.removeItem('exam_media_ready');
                                                        setMediaStatus('denied');
                                                        setMediaMessage('Camera and microphone access are required to start the exam.');
                                                    });
                                            }}
                                            disabled={mediaStatus === 'requesting'}
                                        >
                                            {mediaStatus === 'requesting' ? 'Requesting access...' : 'Retry Permission'}
                                        </button>
                                        <button
                                            type="button"
                                            className="student-quiz-btn primary"
                                            onClick={handleStartTest}
                                            disabled={mediaStatus === 'requesting' || mediaStatus === 'denied' || (instructionTimerEnabled && timeRemaining > 0)}
                                        >
                                                {instructionTimerEnabled && timeRemaining > 0 ? `Start Test (${formatTime(timeRemaining)})` : 'Start Test'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            )}
            {showDeclarationModal ? (
                <div className="declaration-modal-backdrop" style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:2000}}>
                    <div className="declaration-modal" role="dialog" aria-modal="true" aria-labelledby="declaration-title" style={{background:'#fff',padding:20,borderRadius:6,maxWidth:640,width:'90%'}} onClick={(e)=>e.stopPropagation()}>
                        <h3 id="declaration-title">Declaration</h3>
                        <p style={{marginTop:8}}>“I hereby declare that I will not indulge in any unfair practices and will follow all examination guidelines during the assessment.”</p>
                        <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:18}}>
                            <button type="button" className="student-quiz-btn" onClick={()=>setShowDeclarationModal(false)}>Cancel</button>
                            <button type="button" className="student-quiz-btn primary" onClick={proceedStartTest}>I Agree & Start Test</button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default StudentTestInstructionsMain;

